import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLearnWizard } from 'src/hook/components/useLearnWizard';
import { useAds } from 'src/hook/data/useAds';
import { useChallenge } from 'src/hook/data/useChallenge';
import { useHearts } from 'src/hook/data/useHearts';
import { useLearning } from 'src/hook/data/useLearning';
import useUser from 'src/hook/data/useUser';
import { useWordData } from 'src/hook/data/useWordData';
import { useWords } from 'src/hook/data/useWords';
import { useComponentModal } from 'src/hook/modal/useComponentModal';
import usePopUp from 'src/hook/modal/usePopUp';
import { Challenge } from 'src/model/Challenge';
import { AnimationType, HeartType, LearnWizardStep, PageSource } from 'src/model/enum/enums';
import { Word } from 'src/model/Word';
import Spinner from '../ui/Features/Spinner/Spinner';
import HardWordBanner from '../ui/GlobalComponents/HardWordBanner/HardWordBanner';
import { ChallengeHeader } from './ChallengeHeader/ChallengeHeader';
import styles from './Challenges.module.scss';
import NotSure from './ChallengeTypes/NotSure/NotSure';
import RenderChallenge from './RenderChallenge';

const Challenges = () => {
    const { t } = useTranslation();
    const [currentChallenge, setCurrentChallenge] = useState<Challenge>();
    const { forgot, learningInfo } = useLearning();
    const { getWordById } = useWords();
    const { open: openComponentModal } = useComponentModal();
    const { open: openLearnWizard } = useLearnWizard();
    const { fetchWordData } = useWordData();
    const [shuffledChoices, setShuffledChoices] =
        useState<{ isCorrect: boolean, text: string, wordId: number }[]>([]);
    const [correctWord, setCorrectWord] = useState<Word>();
    const { setAutoPlay, getNextChallenge, checkAndUpdateChallengeCount } = useChallenge();
    const [showFinish, setShowFinish] = useState(false);
    const { checkDailyFreeHearts, addHeart, loseHeart, checkUncoveredWordsHearts } = useHearts();
    const isInitialMount = useRef(true);
    const { showHeartPopUp, showAdsPopUp } = usePopUp();
    const { isProUser, userInfo } = useUser();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const isFetching = useRef(false);
    const { setPageSource } = useAds();

    const handleAnswer = async (isCorrect: boolean) => {
        setIsLoading(true);
        setAutoPlay(false);
        if (!isProUser()) {
            if (userInfo?.Hearts === 0) {
                navigate('/earn-heart');
                return;
            }
            else {
                if (checkAndUpdateChallengeCount()) {
                    setPageSource(PageSource.Challenge);
                    showAdsPopUp();
                    return;
                }
            }
        }

        if (!correctWord) {
            setIsLoading(false);
            return;
        }

        const word = getWordById(correctWord?.ID!)!;
        if (isCorrect) {
            openComponentModal('Definition', { word, source: PageSource.Challenge }, AnimationType.SlideUp);
        }
        else {
            const wordData = await fetchWordData(word.ID);
            openLearnWizard(LearnWizardStep.First, { word, wordData, pageSource: PageSource.Challenge });
            forgot(correctWord?.ID!);
        }
        setIsLoading(false);
    };

    const fetchChoices = useCallback(async () => {
        if (isFetching.current) return;
        isFetching.current = true;
        try {
            const result = await getNextChallenge();
            if (!result) {
                setShowFinish(true);
                return;
            }

            setCurrentChallenge(result.challenge);
            const correctWord = getWordById(result.wordId);
            setCorrectWord(correctWord);
            const allChoices = [
                { isCorrect: true, text: result.challenge.Answer, wordId: result.wordId },
                ...result.challenge.Wrong.map(w => ({
                    isCorrect: false,
                    text: w.Text, wordId: w?.Word ?? 0
                }))
            ];

            const shuffled = [...allChoices].sort(() => Math.random() - 0.5);
            setShuffledChoices(allChoices.length > 2 ? shuffled : allChoices);
        } finally {
            setIsLoading(false);
            isFetching.current = false;
        }
    }, [getWordById, getNextChallenge]);

    useEffect(() => {
        if (!learningInfo) return;
        if (learningInfo?.ReviewWordIds.length === 0) {
            setShowFinish(true);
            setIsLoading(false);
        }
        else {
            setShowFinish(false);
            fetchChoices();
        }
    }, [fetchChoices, learningInfo]);

    useEffect(() => {
        if (!learningInfo) return;
        if (isInitialMount.current) {
            if (checkDailyFreeHearts()) {
                showHeartPopUp(HeartType.DailyFree);
            }
            if (checkUncoveredWordsHearts()) {
                setTimeout(() => {
                    showHeartPopUp(HeartType.Discover);
                }, 3000);

            }
            isInitialMount.current = false;
        }
    }, [checkDailyFreeHearts, showHeartPopUp, addHeart, isProUser, learningInfo, checkUncoveredWordsHearts]);

    if (isLoading) {
        return <Spinner fullPage />;
    }

    return (
        <div className={styles.challenges}>
            <ChallengeHeader />
            <HardWordBanner word={correctWord!} pageSource={PageSource.Challenge} />
            <div className={`${styles.challengeContainer}`}>
                <RenderChallenge
                    currentChallenge={currentChallenge}
                    correctWord={correctWord}
                    shuffledChoices={shuffledChoices}
                    onAnswer={handleAnswer}
                    showFinish={showFinish}
                />
            </div>
            <NotSure
                showFinish={showFinish}
                currentChallenge={currentChallenge}
                onAnswer={handleAnswer}
            />
        </div>
    );
}

export default Challenges;
